import { useMemo, useState } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { DsButton, DsSearchInput } from '@/modules/internal-v2/ds';
import { useGetAllContactEmployees } from '@/api/business-partner/business-partner.hooks';
import type { ContactEmployee } from '@/api/contact-employee/contact-employee.types';
import type { ContactListKind } from '@/api/contact-list/contact-list.types';
import { DsTable } from '../../shared/ds-table.component';

interface VerteilerAddContactsProps {
  /** Verteiler kind — picker is filtered to BPs of the matching SAP type. */
  kind: ContactListKind;
  /** `contactEmployeeId`s already in the Verteiler. */
  memberIds: number[];
  /** True while the parent persists the add. */
  saving: boolean;
  /** Commit the checked contacts to the Verteiler. */
  onSubmit: (idsToAdd: number[]) => void;
  /** Leave add mode without committing. */
  onClose: () => void;
}

const KIND_TO_SAP_TYPE: Record<ContactListKind, string> = {
  customer: 'cCustomer',
  supplier: 'cSupplier',
};

const col = createColumnHelper<ContactEmployee>();

/** Prefers a clean "First Last", falling back to the SAP full name. */
function contactLabel(contact: ContactEmployee): string {
  const parts = [contact.firstName, contact.lastName].filter(
    (part): part is string => !!part && part !== 'N/A',
  );
  return parts.length > 0 ? parts.join(' ') : contact.name;
}

function matchesContact(contact: ContactEmployee, term: string): boolean {
  return (
    contactLabel(contact).toLowerCase().includes(term) ||
    contact.name.toLowerCase().includes(term) ||
    (contact.email?.toLowerCase().includes(term) ?? false) ||
    (contact.position?.toLowerCase().includes(term) ?? false) ||
    contact.businessPartner.name.toLowerCase().includes(term)
  );
}

/**
 * Add-mode content of the Verteiler detail page: a proper, virtualized,
 * sortable table over **all** contact employees company-wide. The user
 * filters, multi-selects, and commits the picks in one batch.
 */
export function VerteilerAddContacts({
  kind,
  memberIds,
  saving,
  onSubmit,
  onClose,
}: VerteilerAddContactsProps) {
  const { data: allContacts = [], isPending } = useGetAllContactEmployees();
  const memberSet = useMemo(() => new Set(memberIds), [memberIds]);

  // The backend enforces the kind invariant — we pre-filter here so the
  // picker only ever shows valid candidates for the current Verteiler.
  const sapType = KIND_TO_SAP_TYPE[kind];
  const contacts = useMemo(
    () => allContacts.filter((c) => c.businessPartner.type === sapType),
    [allContacts, sapType],
  );

  const [query, setQuery] = useState('');
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'name', desc: false },
  ]);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return term ? contacts.filter((c) => matchesContact(c, term)) : contacts;
  }, [contacts, query]);

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Select-all applies only to filtered rows that are not yet members.
  const selectableFiltered = useMemo(
    () => filtered.filter((c) => !memberSet.has(c.id)),
    [filtered, memberSet],
  );
  const allSelected =
    selectableFiltered.length > 0 &&
    selectableFiltered.every((c) => selected.has(c.id));

  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) selectableFiltered.forEach((c) => next.delete(c.id));
      else selectableFiltered.forEach((c) => next.add(c.id));
      return next;
    });

  // Columns are rebuilt each render on purpose: the select/select-all cells
  // close over live selection state, so memoizing them would risk stale UI.
  const columns = [
    col.display({
      id: 'select',
      size: 36,
      header: () => (
        <input
          type="checkbox"
          checked={allSelected}
          onChange={toggleAll}
          aria-label="Alle Treffer auswählen"
        />
      ),
      cell: (info) => {
        const contact = info.row.original;
        if (memberSet.has(contact.id)) {
          return <span className="vt-add-tag">✓</span>;
        }
        return (
          <input
            type="checkbox"
            checked={selected.has(contact.id)}
            onChange={() => toggle(contact.id)}
            onClick={(e) => e.stopPropagation()}
          />
        );
      },
    }),
    col.accessor((row) => contactLabel(row), {
      id: 'name',
      header: 'Name',
      meta: { flex: true },
      sortingFn: 'text',
      cell: (info) => (
        <span style={{ fontWeight: 500, color: 'var(--ink)' }}>
          {info.getValue()}
        </span>
      ),
    }),
    col.accessor((row) => row.businessPartner.name, {
      id: 'bp',
      header: 'Firma',
      size: 200,
      sortingFn: 'text',
    }),
    col.accessor('position', {
      header: 'Position',
      size: 150,
      cell: (info) => info.getValue() || '—',
    }),
    col.accessor('email', {
      header: 'E-Mail',
      size: 220,
      cell: (info) => info.getValue() || '—',
    }),
  ];

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => String(row.id),
    enableSortingRemoval: false,
  });

  return (
    <>
      <div className="vt-detail-toolbar">
        <DsSearchInput
          className="vt-detail-search"
          value={query}
          onSearch={setQuery}
          debounce={0}
          placeholder="Ansprechpartner suchen (Name, Firma, E-Mail)…"
          autoFocus
        />
        <div className="vt-toolbar-end">
          <span className="vt-add-count">{selected.size} ausgewählt</span>
          <DsButton
            size="small"
            variant="ghost"
            onClick={onClose}
            disabled={saving}
          >
            Abbrechen
          </DsButton>
          <DsButton
            size="small"
            variant="primary"
            onClick={() => onSubmit([...selected])}
            disabled={selected.size === 0 || saving}
          >
            {saving ? 'Hinzufügen…' : `${selected.size} hinzufügen`}
          </DsButton>
        </div>
      </div>
      <div className="vt-detail-body">
        {isPending ? (
          <div className="bp-empty">
            <div>
              <div className="big">Laden…</div>
            </div>
          </div>
        ) : (
          <DsTable
            table={table}
            fixedLayout
            onRowClick={(contact) => {
              if (!memberSet.has(contact.id)) toggle(contact.id);
            }}
            rowClassName={(contact) =>
              memberSet.has(contact.id) ? 'vt-row-member' : ''
            }
            emptyMessage="Keine Ansprechpartner gefunden."
          />
        )}
      </div>
    </>
  );
}
